import { useCallback, useEffect } from "react";
import axios from "axios";
import Chat from "./ChatModal";
import { APP_PATH } from "../../api/backendPath";
import { isChatOpen } from "../../utils/activeChatRegistry";
import { getAccessToken } from "../../utils/socketAuth";
import useChatDocks, { DOCK_SOURCE, memoizeAsync } from "./useChatDocks";

/**
 * ChatDock — app-wide manager for minimized appointment-chat windows (imaging app).
 *
 * When a patient sends a message for an appointment (dispatched as the
 * `chat-incoming-message` window event by useSocketNotifications), a minimized
 * chat bubble appears in the bottom-right corner showing the unread count.
 * Clicking the bubble expands the full (existing) ChatModal for that appointment;
 * the modal's close button collapses it back to a bubble.
 *
 * Reuses ChatModal as-is: while minimized the modal stays mounted (it renders no
 * UI when `isOpen` is false) so it keeps counting unread via `onUnreadChange`, and
 * marks the room read when expanded.
 */
const keyOf = (d) => String(d.appointmentId ?? d.roomId ?? d.patientUserId);

const authHeaders = () => ({ Authorization: `Bearer ${getAccessToken()}` });

// Patient name + avatar by user id (works for general chats with no order too).
const lookupParticipant = memoizeAsync(async (patientUserId) => {
  const res = await axios.get(
    `${process.env.REACT_APP_API_URL}${APP_PATH.chatParticipant}`,
    { headers: authHeaders(), params: { user_id: patientUserId } },
  );
  const p = res?.data?.data || res?.data || {};
  return {
    name:
      p.name ||
      [p.full_name, p.surname].filter(Boolean).join(" ").trim() ||
      null,
    avatar: p.avatar_presigned_url || p.avatarPresignedUrl || p.avatar || null,
  };
});

// Patient name, avatar and booking number ("IMG-0001") from the imaging order, so the header
// reads like the Order Management rows.
const lookupOrder = memoizeAsync(async (appointmentId) => {
  const res = await axios.get(
    `${process.env.REACT_APP_API_URL}${APP_PATH.imagingOrders}/${appointmentId}`,
    { headers: authHeaders() },
  );
  const order = res?.data?.data || res?.data || {};
  const p = order.patient || {};
  // The imaging order detail endpoint returns camelCase fields. Keep snake_case
  // fallbacks so this keeps working if the API shape ever changes.
  return {
    name:
      p.name ||
      [p.full_name, p.surname].filter(Boolean).join(" ").trim() ||
      order.patient_name ||
      null,
    // Never fall back to the numeric primary key.
    number:
      order.bookingNumber || order.booking_number || order.order_number || "",
    avatar:
      p.avatarPresignedUrl ||
      p.avatar_presigned_url ||
      p.avatar ||
      order.avatarPresignedUrl ||
      order.avatar_presigned_url ||
      null,
  };
});

// Both lookups run in parallel; the order wins for name/avatar, the participant fills gaps
// (no appointment id, or not an imaging order).
const resolveHeader = async ({ patientUserId, appointmentId }) => {
  const [order, person] = await Promise.all([
    appointmentId ? lookupOrder(appointmentId).catch(() => null) : null,
    patientUserId ? lookupParticipant(patientUserId).catch(() => null) : null,
  ]);
  return {
    title: order?.name || person?.name || null,
    subtitle: order?.number || "",
    avatar: order?.avatar || person?.avatar || null,
  };
};

const ChatDock = () => {
  const { docks, open, update, remove, expand, minimize } = useChatDocks({
    keyOf,
    resolveHeader,
  });

  const onIncoming = useCallback(
    (e) => {
      const det = e.detail || {};
      const patientUserId = det.sender_id;
      const appointmentId = det.appointment_id ?? null;
      const roomId = det.room_id ?? null;
      if (!patientUserId) return; // need someone to chat with
      // Already open in a page-level chat window: the message is shown there.
      if (
        isChatOpen(
          { roomId, appointmentId, senderId: patientUserId },
          { excludeSource: DOCK_SOURCE },
        )
      )
        return;
      open({
        patientUserId,
        appointmentId,
        roomId,
        title: det.sender_name || "Patient",
        // Filled by resolveHeader with the booking number (IMG-0001).
        subtitle: "",
        avatar: null,
      });
    },
    [open],
  );

  useEffect(() => {
    window.addEventListener("chat-incoming-message", onIncoming);
    return () =>
      window.removeEventListener("chat-incoming-message", onIncoming);
  }, [onIncoming]);

  // Bubbles appear once their header is resolved (see useChatDocks).
  const minimized = docks.filter((d) => !d.expanded && d.ready);


  return (
    <>
      {/* Keep every dock's ChatModal mounted: hidden UI when minimized (still counts
          unread), full panel when expanded (its own fixed bottom-right styling). */}
      {docks.map((d) => {
        const id = keyOf(d);
        return (
          <Chat
            key={`chat-${id}`}
            chatSource={DOCK_SOURCE}
            isOpen={d.expanded}
            patientUserId={d.patientUserId}
            bookingId={d.appointmentId}
            initialRoomId={d.roomId}
            title={d.title}
            subtitle={d.subtitle}
            onClose={() => minimize(id)}
            onUnreadChange={(n) => update(id, { count: n })}
          />
        );
      })}

      {/* Minimized bubbles, stacked upward in the bottom-right corner. */}
      {minimized.map((d, idx) => {
        const id = keyOf(d);
        return (
          <div
            key={`dock-${id}`}
            onClick={() => expand(id)}
            style={{
              position: "fixed",
              right: "1rem",
              bottom: `${1 + idx * 4.25}rem`,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "#0d9488",
              color: "#fff",
              padding: "8px 12px",
              borderRadius: 30,
              boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
              cursor: "pointer",
              maxWidth: 280,
            }}
            title="Open chat"
          >
            {/* No patient photo; the unread badge sits on the bubble's corner. */}
            {d.count > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  minWidth: 18,
                  height: 18,
                  padding: "0 4px",
                  borderRadius: 9,
                  background: "#e53935",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: "18px",
                  textAlign: "center",
                  boxShadow: "0 0 0 2px #0d9488",
                }}
              >
                {d.count > 99 ? "99+" : d.count}
              </span>
            )}
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 1.2,
                overflow: "hidden",
                maxWidth: 170,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {d.title}
              </span>
              {d.subtitle ? (
                <span
                  style={{
                    fontSize: 11,
                    opacity: 0.85,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {d.subtitle}
                </span>
              ) : null}
            </span>
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                remove(id);
              }}
              aria-label="Dismiss chat"
              style={{
                marginLeft: 2,
                background: "rgba(255,255,255,0.2)",
                border: "none",
                color: "#fff",
                width: 22,
                height: 22,
                borderRadius: "50%",
                cursor: "pointer",
                lineHeight: "20px",
                fontSize: 14,
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </>
  );
};

export default ChatDock;
